import React, { useEffect, useState } from 'react'
import Header from '../../../components/dms_common/Header';
import { Loader } from '../../../components/loader/Loader';
import CustomDataGrid from '../../../components/dms_common/CustomDataGrid';
import { AuditTrailsRowsData} from '../../../types/types';
import {  auditTrailsMetadata, getAuditTrailsStockdata, statusMapping, deriveStatus } from './AuditTrailUtils';
import { getDataFromLocalDb } from '../../../services/dbService';
import ScreenOverlay from '../../../components/dms_common/ScreenOverlay';
import { logAnalyticsEvent, logLoadTrace } from '../../../firebase';
import dayjs, { Dayjs } from "dayjs";
import DateFilter from '../../../features/deliveryConfirmation/DateFilter';
import './AuditTrails.css';
import { Button } from '@mui/material';
import { ReactComponent as DownloadAllIcon } from "../../../assets/image/svg/downloadAllWhite.svg";
import { Product } from '../../createSecondaryOrder/models';
import { getMergedCatalogueAndRealTimeStockData } from '../RealTimeStock/RealTimeStockUtils';

const AuditTrails = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [allData, setAllData] = useState<AuditTrailsRowsData[]>([]);
    const [filteredData, setFilteredData] = useState<AuditTrailsRowsData[]>([]);
    const [searchText, setSearchText] = useState<string>("");
    const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
      startDate: dayjs().subtract(1, 'day').format("YYYY-MM-DD"),
      endDate: dayjs().subtract(1, 'day').format("YYYY-MM-DD"),
    });


    const getInitialTableData = async () => {
      try {
        setIsLoading(true);
        const response = await getAuditTrailsStockdata(dateRange.startDate, dateRange.endDate);
        if (response.length > 0) {
          let auditTrailsData: AuditTrailsRowsData[] = [];
          
          // Simple counter for unique IDs
          let idCounter = 0;
          
          response.forEach((item: any) => {
            let adjustedFromStatus = item?.fromstatus;
            let adjustedToStatus = item?.tostatus;

            if (item?.action?.toLowerCase() === "primary grn accepted" && !item?.fromstatus) {
              adjustedFromStatus = "New Stock";
            }

            if (item?.action?.toLowerCase() === "secondary order delivered" && !item?.tostatus) {
              adjustedToStatus = "Removed from Inventory";
            }

            if (item?.action?.toLowerCase() === "primary grn partially accepted" && !item?.fromstatus) {
              adjustedFromStatus = "New Stock";
            }

            if (item?.action?.toLowerCase() === "secondary order partially delivered" && !item?.tostatus) {
              adjustedToStatus = "Removed from Inventory";
            }


            auditTrailsData.push({
              id: String(idCounter++),
              skuCode: item?.skucode ?? '',
              batchId: item?.batchid ?? 'N/A',
              userId: item?.userid ?? '',
              transactionType: item?.action ?? '',
              stockTypeTo: capitalizeFirstLetter(adjustedToStatus),
              stockTypeFrom: capitalizeFirstLetter(adjustedFromStatus),
              status: deriveStatus(
                capitalizeFirstLetter(adjustedFromStatus),
                capitalizeFirstLetter(adjustedToStatus),
                item?.reverttype
              ),
              timestamp: item?.timestamp ?? '',
              caseQty: item?.caseqty ?? 0,
              pieceQty: item?.pieceqty ?? 0,
              otherQty: item?.otherqty ?? 0,
              currentCaseQty: item?.currentcaseqty ?? 0,
              currentPieceQty: item?.currentpieceqty ?? 0,
              currentOtherQty: item?.currentotherqty ?? 0,
              transactionId: item?.requestid ?? '',
              revertType: item?.reverttype ?? ''
            });
          });
          
          const productsData = await getDataFromLocalDb("Product");
          const productsMap: Record<string, Product> = productsData?.data?.[0]?.data ?? {};
          const skuProductMap: Record<string, string> = {};
          Object.values(productsMap).forEach((item) => {
            skuProductMap[item.batchCode] = item.skuDescription;
          });
          
          const enrichedData = auditTrailsData.map(row => ({
            ...row,
            skuName: skuProductMap[row.skuCode] || ""
          }));

          const mergedData= await getMergedCatalogueAndRealTimeStockData(
            enrichedData,
            productsMap
          )
          
          setAllData(mergedData);
          setFilteredData(mergedData);
          logAnalyticsEvent( "stock_audit_trail_viewed", {
            "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
            "total_logs_displayed":enrichedData.length
          });
        } else {
          setAllData([]);
          setFilteredData([]);
          logAnalyticsEvent( "stock_audit_trail_viewed", {
            "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
            "total_logs_displayed":0
          });
        }
      } catch (error) {
        setAllData([]);
        setFilteredData([]);
        logAnalyticsEvent( "stock_audit_trail_viewed", {
          "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
          "total_logs_displayed":0
        });
        console.error("Error fetching Audit Trails Data", error);
      } finally {
        setIsLoading(false);
      }
    };

    const handleApply = (startDate: Dayjs, endDate: Dayjs) => {
      setDateRange({
        startDate: startDate.format("YYYY-MM-DD"),
        endDate: endDate.format("YYYY-MM-DD"),
      });
    };
    
    const capitalizeFirstLetter = (str?: string | null): string =>
      str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : 'N/A';

    const handleReset = () => {
      const yesterday = dayjs().subtract(1, 'day');
      setDateRange({
        startDate: yesterday.format("YYYY-MM-DD"),
        endDate: yesterday.format("YYYY-MM-DD"),
      });
    };
    
    const handleDownloadAll = () => {
      console.log("Download All clicked!", dateRange);
    };
    
    const init = async () => {
      setIsLoading(true);
      await getInitialTableData();
      setIsLoading(false);
    };
  
    useEffect(() => {
      logLoadTrace(init,"audit_trails_page_load_time");
    }, [dateRange]);
    
    const handleSearchValueChange = (value: string) => {
      setSearchText(value);
    };
  
    useEffect(() => {
      if (searchText.trim()) {
        const searchKey = searchText.toLowerCase().trim();
        
        // Function to normalize text by removing spaces
        const normalizeText = (text: string = ''): string => {
          return text.toLowerCase().replace(/\s+/g, '');
        };
        
        // Normalize the search key
        const normalizedSearchKey = normalizeText(searchKey);
        
        const filtered = allData.filter(
          (item) => {
            // Check the standard way first
            const standardMatch = 
              (item.transactionType || '').toLowerCase().includes(searchKey) ||
              (item.skuCode || '').toLowerCase().includes(searchKey) ||
              (item.skuName || '').toLowerCase().includes(searchKey) ||
              (item.batchId || '').toLowerCase().includes(searchKey) ||
              (item.userId || '').toLowerCase().includes(searchKey) ||
              (item.transactionId || '').toLowerCase().includes(searchKey);
            
            // If standard match is found, return immediately
            if (standardMatch) return true;
            
            // Check with normalized text (spaces removed)
            return (
              normalizeText(item.transactionType).includes(normalizedSearchKey) ||
              normalizeText(item.skuCode).includes(normalizedSearchKey) ||
              normalizeText(item.skuName).includes(normalizedSearchKey) ||
              normalizeText(item.batchId).includes(normalizedSearchKey) ||
              normalizeText(item.userId).includes(normalizedSearchKey) ||
              normalizeText(item.transactionId).includes(normalizedSearchKey)
            );
          }
        );
        setFilteredData(filtered);
      } else {
        setFilteredData(allData);
      }
    }, [searchText, allData]);
  
    return (
      <div className="audit-trails">
        <ScreenOverlay apply={isLoading} />
        <Header
          title={"Audit Trails"}
          searchFunction={handleSearchValueChange}
          onRefresh={init}
        />
        <div className="date-filter-container">
          <div className="filter-section">
            <div>
              <DateFilter onApply={handleApply} onReset={handleReset} initialDayOffset={1} endDayOffset={1} maxDays={90} futureDateAllowed={false} />
            </div>
            {/* <Button
            variant="contained"
            sx={{
              textTransform: "none",
              backgroundColor: "var(--dashboard-theme-color)",
              "&:hover": {
                boxShadow: "none",
                background: "var(--dashboard-theme-color)",
              },
            }}
            onClick={handleDownloadAll}
          >
            <DownloadAllIcon style={{ marginRight: "6px" }} />
            Download All
          </Button> */}
          </div>
        </div>
        <div className="info-section">
          <p className="info_text">
            By default, yesterday's audit trails are displayed. Use the date filter above to view data for different dates.
          </p>
        </div>
        {isLoading ? (
          <Loader sx={{ position: "absolute", top: "45%" }} />
        ) : (
          <CustomDataGrid
            rows={filteredData}
            columnsMetaData={auditTrailsMetadata}
            orderStatusConfig={statusMapping}
            horizontalScrollBehaviour={true}
            noDataMessage="Audit Trails data not found."
            enableRowClickRedirect={false} 
          />
        )}
      </div>
    );
}

export default AuditTrails;
